import { z } from 'zod';
import { TagSchema } from './hotel-media';

export const VocabCategorySchema = z.object({
  id: z.string().guid(),
  key: z.string(),
  label: z.string(),
  position: z.number().int(),
  visionEligible: z.boolean(),
  isSystem: z.boolean(),
  archivedAt: z.string().datetime().nullable(),
});

export const VocabTagSchema = z.object({
  id: z.string().guid(),
  tag: z.string(),
  categoryId: z.string().guid(),
  categoryKey: z.string(),
  archivedAt: z.string().datetime().nullable(),
  /**
   * A word whose presence on an image makes an admin override demand a
   * written note. Lives on the dictionary row so a rename carries it along.
   */
  isSensitive: z.boolean(),
  /** How many images across the org carry this tag. Only the editor asks for it. */
  usageCount: z.number().int().optional(),
});

export const VocabSchema = z.object({
  categories: z.array(VocabCategorySchema),
  tags: z.array(VocabTagSchema),
});
export type Vocab = z.infer<typeof VocabSchema>;

export const CreateVocabCategorySchema = z.object({
  label: z.string().min(1).max(60),
  visionEligible: z.boolean().default(true),
});
export const PatchVocabCategorySchema = z
  .object({
    label: z.string().min(1).max(60).optional(),
    position: z.number().int().min(0).max(999).optional(),
    visionEligible: z.boolean().optional(),
    archived: z.boolean().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'empty patch' });

export const CreateVocabTagSchema = z.object({
  tag: TagSchema,
  categoryId: z.string().guid(),
});
export const PatchVocabTagSchema = z
  .object({
    /** Renaming rewrites every image that carries the old word (spec 9.2). */
    tag: TagSchema.optional(),
    categoryId: z.string().guid().optional(),
    archived: z.boolean().optional(),
    /** Flips the sensitive marker (spec 9.3 admin editor). */
    isSensitive: z.boolean().optional(),
    /** Required acknowledgement when the new name already exists (merge). */
    confirmMerge: z.boolean().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'empty patch' });
export type PatchVocabTag = z.infer<typeof PatchVocabTagSchema>;
